<template>
  <div class="tw-max-w-6xl tw-mx-auto tw-p-4">
    <div
      class="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2 tw-mb-3"
    >
      <h1 class="tw-text-xl tw-font-bold tw-text-slate-800">
        🛟 Pannello Assistenza — Console supporto
      </h1>
      <div class="tw-flex tw-gap-1 tw-text-xs tw-flex-wrap">
        <button
          v-for="f in filters"
          :key="f.value"
          class="tw-px-3 tw-py-1 tw-rounded-lg tw-border"
          :class="
            filter === f.value
              ? 'tw-bg-fb-accent tw-text-white tw-border-fb-accent'
              : 'tw-bg-white tw-text-slate-600 tw-border-slate-300 hover:tw-bg-slate-50'
          "
          @click="setFilter(f.value)"
        >
          {{ f.label }}
        </button>
      </div>
    </div>

    <div
      class="tw-flex tw-flex-col md:tw-flex-row tw-gap-4 tw-h-[72vh] tw-min-h-0"
    >
      <!-- Lista -->
      <div
        class="tw-min-h-0 md:tw-w-2/5 tw-bg-white tw-border tw-border-slate-200 tw-rounded-xl tw-p-3 md:tw-flex md:tw-flex-col"
        :class="view === 'list' ? 'tw-flex tw-flex-col tw-flex-1' : 'tw-hidden'"
      >
        <p
          v-if="!list.length"
          class="tw-text-sm tw-text-slate-400 tw-text-center tw-py-8"
        >
          Nessun ticket.
        </p>
        <div
          class="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-pr-1 tw-divide-y tw-divide-slate-100"
        >
          <button
            v-for="t in list"
            :key="t.id"
            type="button"
            class="tw-w-full tw-text-left tw-px-2 tw-py-2.5 tw-border-l-2 tw-transition hover:tw-bg-slate-50"
            :class="
              selectedId === t.id
                ? 'tw-border-fb-accent tw-bg-slate-50'
                : 'tw-border-transparent'
            "
            @click="selectTicket(t.id)"
          >
            <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
              <span
                class="tw-text-sm tw-font-medium tw-truncate tw-flex tw-items-center tw-gap-1.5"
              >
                <span
                  v-if="t.unreadAdmin"
                  class="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-amber-500 tw-shrink-0"
                ></span>
                {{ catMeta(t.category).icon }}
                {{ t.author ? t.author.email : "—" }}
              </span>
              <span
                class="tw-text-[11px] tw-shrink-0 tw-rounded-full tw-px-2 tw-py-0.5 tw-font-medium"
                :class="statusTone(t.status)"
                >{{ statusLabel(t.status, "admin") }}</span
              >
            </div>
            <p class="tw-text-xs tw-text-slate-500 tw-mt-0.5 tw-line-clamp-1">
              {{ t.latestMessage ? t.latestMessage.body : t.preview }}
            </p>
            <div class="tw-flex tw-items-center tw-justify-between tw-mt-0.5">
              <span class="tw-text-[11px] tw-text-slate-400">{{
                formatDate(t.lastMessageAt || t.createdAt)
              }}</span>
              <span
                v-if="t.unreadAdmin"
                class="tw-text-[11px] tw-font-medium tw-text-amber-600"
                >da leggere</span
              >
            </div>
          </button>
        </div>
      </div>

      <!-- Dettaglio conversazione -->
      <div
        class="tw-min-h-0 md:tw-flex-1 tw-bg-white tw-border tw-border-slate-200 tw-rounded-xl tw-p-3 md:tw-flex md:tw-flex-col"
        :class="
          view === 'detail' ? 'tw-flex tw-flex-col tw-flex-1' : 'tw-hidden'
        "
      >
        <div
          v-if="!currentTicket && !loadingDetail"
          class="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-sm tw-text-slate-400"
        >
          Seleziona un ticket per gestirlo.
        </div>
        <div
          v-else-if="loadingDetail"
          class="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-sm tw-text-slate-400"
        >
          Caricamento…
        </div>

        <template v-else>
          <div
            class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-2 tw-shrink-0"
          >
            <div class="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
              <button
                type="button"
                class="md:tw-hidden tw-text-sm tw-text-slate-500"
                @click="view = 'list'"
              >
                ‹ Lista
              </button>
              <span class="tw-font-semibold tw-text-slate-800 tw-truncate">
                {{ catMeta(currentTicket.category).icon }}
                {{ currentTicket.author ? currentTicket.author.email : "—" }}
              </span>
              <span
                class="tw-text-[11px] tw-rounded-full tw-px-2 tw-py-0.5"
                :class="statusTone(currentTicket.status)"
              >
                {{ statusLabel(currentTicket.status, "admin") }}
              </span>
            </div>
            <div class="tw-flex tw-gap-2 tw-shrink-0">
              <button
                v-if="!currentTicket.resolved"
                type="button"
                class="tw-text-xs tw-bg-emerald-600 tw-text-white tw-rounded tw-px-3 tw-py-1 hover:tw-opacity-90 disabled:tw-opacity-60"
                :disabled="updating"
                @click="setResolved(true)"
              >
                Segna risolto
              </button>
              <button
                v-else
                type="button"
                class="tw-text-xs tw-bg-slate-300 tw-text-slate-700 tw-rounded tw-px-3 tw-py-1 hover:tw-opacity-90 disabled:tw-opacity-60"
                :disabled="updating"
                @click="setResolved(false)"
              >
                Riapri
              </button>
            </div>
          </div>
          <p class="tw-text-[11px] tw-text-slate-400 tw-mb-2 tw-shrink-0">
            Aperto il {{ formatDate(currentTicket.createdAt) }}
            <span v-if="currentTicket.pageUrl">
              · {{ currentTicket.pageUrl }}</span
            >
          </p>

          <!-- Log console del browser allegati (solo supporto tecnico) -->
          <div
            v-if="
              currentTicket.clientLogs &&
              currentTicket.clientLogs.logs &&
              currentTicket.clientLogs.logs.length
            "
            class="tw-mb-2 tw-shrink-0 tw-border tw-border-slate-200 tw-rounded-lg"
          >
            <button
              type="button"
              class="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-bg-slate-50"
              @click="showLogs = !showLogs"
            >
              <span
                >🪵 Log browser allegati ({{
                  currentTicket.clientLogs.logs.length
                }})</span
              >
              <span>{{ showLogs ? "▲" : "▼" }}</span>
            </button>
            <pre
              v-if="showLogs"
              class="tw-max-h-40 tw-overflow-auto tw-text-[10px] tw-leading-snug tw-bg-slate-900 tw-text-slate-100 tw-p-2 tw-rounded-b-lg tw-whitespace-pre-wrap tw-break-words"
              >{{ formatLogs(currentTicket.clientLogs.logs) }}</pre
            >
          </div>

          <FeedbackConversation
            class="tw-flex-1 tw-min-h-0"
            role="admin"
            :messages="currentTicket.messages || []"
            :can-reply="canReply"
            :sending="sending"
            @send="onSend"
          >
            <template #closed>
              <button
                v-if="currentTicket.resolved"
                type="button"
                class="tw-text-xs tw-bg-fb-accent tw-text-white tw-rounded-lg tw-px-3 tw-py-1.5 hover:tw-bg-fb-accent-hover"
                :disabled="updating"
                @click="setResolved(false)"
              >
                Riapri per rispondere
              </button>
            </template>
          </FeedbackConversation>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
// FeedbackPanelPage — Console del supporto tecnico (dev/support del modulo Assistenza).
// NB: "admin" nelle label/ruoli = supporto tecnico del modulo, NON admin del CRM cliente.
// Accesso già filtrato a monte da canAccessPanel (route beforeEnter).
import feedbackService, {
  categoryMeta,
  statusLabel,
  statusTone,
} from "../services/feedbackService";
import FeedbackConversation from "../components/FeedbackConversation.vue";

export default {
  name: "FeedbackPanelPage",
  components: { FeedbackConversation },
  inject: ["feedbackConfig"],
  data() {
    return {
      list: [],
      loading: false,
      filter: "all",
      filters: [
        { value: "all", label: "Tutti" },
        { value: "waiting_admin", label: "Da rispondere" },
        { value: "waiting_user", label: "In attesa utente" },
        { value: "resolved", label: "Risolti" },
      ],
      view: "list",
      selectedId: null,
      currentTicket: null,
      loadingDetail: false,
      sending: false,
      updating: false,
      showLogs: false,
      timer: null,
      activeInterval: 12000,
      hiddenInterval: 60000,
      pollingInFlight: false,
      notifiedNew: new Set(),
      firstLoad: true,
    };
  },
  computed: {
    canReply() {
      const t = this.currentTicket;
      return !!t && !t.resolved && t.status !== "archived" && !t.expired;
    },
  },
  mounted() {
    this.refreshNow();
    document.addEventListener("visibilitychange", this.onVisible);
    window.addEventListener("focus", this.refreshNow);
  },
  beforeUnmount() {
    this.stopPolling();
    document.removeEventListener("visibilitychange", this.onVisible);
    window.removeEventListener("focus", this.refreshNow);
  },
  methods: {
    notify(msg, opts) {
      this.feedbackConfig?.notify?.(msg, opts);
    },
    stopPolling() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
    computeInterval() {
      return typeof document !== "undefined" && document.hidden
        ? this.hiddenInterval
        : this.activeInterval;
    },
    scheduleNext() {
      this.stopPolling();
      this.timer = setTimeout(this.tick, this.computeInterval());
    },
    async tick() {
      await this.load();
      this.scheduleNext();
    },
    refreshNow() {
      this.load();
      this.scheduleNext();
    },
    onVisible() {
      if (document.visibilityState === "visible") this.refreshNow();
    },
    catMeta(v) {
      return categoryMeta(v);
    },
    statusLabel,
    statusTone,
    formatDate(d) {
      try {
        return new Date(d).toLocaleString("it-IT");
      } catch {
        return "";
      }
    },
    formatLogs(logs = []) {
      return (logs || [])
        .map((l) => {
          const t = l.timestamp
            ? new Date(l.timestamp).toLocaleTimeString("it-IT")
            : "";
          const lvl = (l.level || "log").toUpperCase();
          const stack = l.stack ? `\n  ${l.stack}` : "";
          return `[${t}] ${lvl}: ${l.message || ""}${stack}`;
        })
        .join("\n");
    },
    setFilter(v) {
      this.filter = v;
      this.notifiedNew = new Set();
      this.firstLoad = true;
      this.refreshNow();
    },
    async load() {
      if (this.pollingInFlight) return;
      this.pollingInFlight = true;
      this.loading = true;
      try {
        const { data } = await feedbackService.panelList({
          filter: this.filter,
        });
        const rows = data.feedback || [];
        const currentNew = rows.filter((t) => t.unreadAdmin).map((t) => t.id);
        const fresh = currentNew.filter((id) => !this.notifiedNew.has(id));
        if (!this.firstLoad && fresh.length > 0)
          this.notify("📩 Nuova segnalazione ricevuta", { type: "info" });
        this.notifiedNew = new Set(currentNew);
        this.firstLoad = false;
        this.list = rows;
        if (this.view === "detail" && this.selectedId && this.currentTicket) {
          const summary = rows.find((t) => t.id === this.selectedId);
          if (
            summary &&
            summary.lastMessageAt !== this.currentTicket.lastMessageAt
          )
            this.loadDetail(this.selectedId, true);
        }
      } catch (err) {
        if (err?.response?.status === 401) this.stopPolling();
        else
          this.notify(
            err?.response?.data?.message || "Errore nel caricamento.",
            { type: "error" }
          );
      } finally {
        this.loading = false;
        this.pollingInFlight = false;
      }
    },
    notifyWidgetRefresh() {
      window.dispatchEvent(
        new CustomEvent("feedback:refresh", { detail: { silent: true } })
      );
    },
    async loadDetail(id, silent = false) {
      if (!silent) this.loadingDetail = true;
      try {
        const { data } = await feedbackService.panelGetTicket(id);
        this.currentTicket = data.ticket;
        this.notifyWidgetRefresh();
      } catch (err) {
        this.notify(err?.response?.data?.message || "Errore nel ticket.", {
          type: "error",
        });
      } finally {
        this.loadingDetail = false;
      }
    },
    selectTicket(id) {
      this.selectedId = id;
      this.view = "detail";
      this.currentTicket = null;
      this.showLogs = false;
      this.loadDetail(id);
    },
    async onSend({ body, files, resetDraft }) {
      if (!this.selectedId) return;
      this.sending = true;
      try {
        const { data } = await feedbackService.panelAddMessage(
          this.selectedId,
          { body, files }
        );
        this.currentTicket = data.ticket;
        resetDraft?.();
        this.refreshNow();
        this.notifyWidgetRefresh();
      } catch (err) {
        this.notify(err?.response?.data?.message || "Invio non riuscito.", {
          type: "error",
        });
      } finally {
        this.sending = false;
      }
    },
    async setResolved(resolved) {
      if (!this.selectedId) return;
      this.updating = true;
      try {
        const { data } = await feedbackService.panelUpdate(this.selectedId, {
          resolved,
        });
        this.currentTicket = data.ticket;
        this.refreshNow();
        this.notifyWidgetRefresh();
      } catch (err) {
        this.notify(
          err?.response?.data?.message || "Aggiornamento non riuscito.",
          { type: "error" }
        );
      } finally {
        this.updating = false;
      }
    },
  },
};
</script>
